import React, { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import { VOWELS, CONSONANTS } from '@/types';
import { shuffleArray } from '@/utils/array';
import { LETTERS_WITH_DESCENDERS } from '@/utils/letters';
import { Draggable, DropBin, Celebrate } from '@/components';

type LetterType = 'vowels' | 'consonants';

/**
 * getLetterSetFromWords
 *
 * Returns a set of unique letters from the words.
 * Ignores repeated letters within the same word.
 */

const getLetterSetFromWords = (
  words: Array<string>,
  options: {
    type?: LetterType;
    minLength?: number;
    allowedLetters?: Array<string>;
  } = {},
) => {
  const allowedSet = new Set(options.allowedLetters);

  const vowels = new Set(VOWELS.filter((letter) => allowedSet.has(letter)));
  const consonants = new Set(
    CONSONANTS.filter((letter) => allowedSet.has(letter)),
  );

  const uniqueLetters = new Set<string>();

  // Extract unique letters from words
  for (const word of words) {
    for (const letter of word.toLowerCase()) {
      if (!allowedSet.size || allowedSet.has(letter)) {
        uniqueLetters.add(letter);
      }
    }
  }

  // Filter by type
  const letters = [...Array.from(uniqueLetters)].filter((letter) =>
    options.type === 'vowels'
      ? vowels.has(letter)
      : options.type === 'consonants'
        ? consonants.has(letter)
        : true,
  );

  // Pad if needed, without duplicates
  if (options.minLength && letters.length < options.minLength) {
    const padPool = (
      options.type === 'vowels'
        ? [...Array.from(vowels)]
        : options.type === 'consonants'
          ? [...Array.from(consonants)]
          : options.allowedLetters || []
    ).filter((l) => !letters.includes(l)); // prevent duplicates

    for (const extraLetter of padPool) {
      letters.push(extraLetter);
      if (letters.length >= options.minLength) {
        break;
      }
    }

    while (letters.length < options.minLength) {
      letters.push('_');
    }
  }

  return shuffleArray(letters);
};

interface GrabGameSpellProps {
  word: string;
  allWords: Array<string>;
  spelt: Array<string | undefined>;
  setSpelt: (spelt: Array<string | undefined>) => void;
  allowedLetters: Array<string>;
  onCorrect?: () => void;
}

type SelectedLetter = {
  index: number;
  letterType: LetterType;
};

export const GrabGameSpell = ({
  word,
  allWords,
  spelt,
  setSpelt,
  allowedLetters,
  onCorrect,
}: GrabGameSpellProps) => {
  const [showingCorrect, setShowingCorrect] = useState(false);

  const [selectedLetter, setSelectedLetter] = useState<
    SelectedLetter | undefined
  >();

  const status: 'correct' | 'incorrect' | 'incomplete' = useMemo(() => {
    if (spelt.join('') === word) {
      return 'correct';
    }
    if (spelt.join('').length === word.length && spelt.join() !== word) {
      return 'incorrect';
    }

    return 'incomplete';
  }, [spelt, word]);

  const vowels = useMemo(
    () =>
      getLetterSetFromWords(allWords, {
        type: 'vowels',
        minLength: 4,
        allowedLetters,
      }),
    [allWords, allowedLetters],
  );

  const consonants = useMemo(
    () =>
      getLetterSetFromWords(allWords, {
        type: 'consonants',
        minLength: 6,
        allowedLetters,
      }),
    [allWords, allowedLetters],
  );

  const getSelectedLetter = () => {
    if (selectedLetter?.letterType === 'vowels') {
      return vowels[selectedLetter.index];
    }

    if (selectedLetter?.letterType === 'consonants') {
      return consonants[selectedLetter.index];
    }

    return;
  };

  useEffect(() => {
    if (status === 'correct' && onCorrect) {
      setShowingCorrect(true);

      setTimeout(() => {
        onCorrect();
        setShowingCorrect(false);
      }, 3000);
    }
  }, [allWords, word, status, onCorrect]);

  return (
    <div className="flex flex-col gap-8 justify-center h-full">
      <div className="relative flex gap-4 justify-center">
        {Array.from({ length: word.length }).map((_, index) => (
          <DropBin
            key={index}
            className={clsx(
              'w-36 h-36 xl:w-48 xl:h-48 px-2 flex items-center justify-center border-4 border-solid font-sassoonInfant',
              'text-[140px] xl:text-[200px] text-left transition-colors duration-300 ease-in-out',
              showingCorrect
                ? 'bg-[var(--c-success)] border-[var(--c-success)] text-white'
                : 'border-pt-primary-accent',
            )}
            value={spelt[index]}
            setValue={(letter) => {
              const updated = [...spelt];
              updated[index] = letter;
              setSpelt(updated);
            }}
            showValue
            onClick={() => {
              if (selectedLetter) {
                const updated = [...spelt];
                updated[index] = getSelectedLetter();
                setSpelt(updated);
                setSelectedLetter(undefined);
              }
            }}
          />
        ))}

        {status === 'correct' && (
          <Celebrate className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        )}
      </div>

      <div className="flex gap-6 flex-wrap justify-center">
        {vowels.map((letter, index) => (
          <Draggable
            key={index}
            className={clsx(
              'relative w-24 h-24 xl:w-28 xl:h-28 shadow-inner-xl',
              'flex items-center justify-center font-sassoonInfant',
              'bg-white rounded-xl text-6xl xl:text-8xl',
              selectedLetter?.letterType === 'vowels' &&
                selectedLetter?.index === index &&
                'border-4 border-solid border-pt-primary',
            )}
            value={letter}
            onClick={() =>
              setSelectedLetter({
                index,
                letterType: 'vowels',
              })
            }
          >
            <span
              className={clsx(
                LETTERS_WITH_DESCENDERS.includes(letter) && 'relative -top-9',
              )}
            >
              {letter}
            </span>
          </Draggable>
        ))}
      </div>

      <div className="flex gap-6 flex-wrap justify-center">
        {consonants.map((letter, index) => (
          <Draggable
            key={index}
            className={clsx(
              'relative w-24 h-24 xl:w-28 xl:h-28 shadow-inner-xl',
              'flex items-center justify-center font-sassoonInfant',
              'bg-white rounded-xl text-6xl xl:text-8xl',
              selectedLetter?.letterType === 'consonants' &&
                selectedLetter?.index === index &&
                'border-4 border-solid border-pt-primary',
              LETTERS_WITH_DESCENDERS.includes(letter) && 'pt-4',
            )}
            value={letter}
            onClick={() =>
              setSelectedLetter({
                index,
                letterType: 'consonants',
              })
            }
          >
            <span
              className={clsx(
                LETTERS_WITH_DESCENDERS.includes(letter) && 'relative -top-9',
              )}
            >
              {letter}
            </span>
          </Draggable>
        ))}
      </div>
    </div>
  );
};
